/**
 * @fileoverview Workspace Key Dates Calendar
 *
 * URL: /dashboard/calendar
 *
 * Aggregates key dates across every job in the active organisation
 * that the current user is a member of. Includes a job multi-select
 * filter so the practitioner can narrow to a single matter without
 * leaving the workspace view.
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ACTIVE_JOB_STATUSES } from "@cvx/jobs/schema";
import type { JobStatus } from "@cvx/jobs/schema";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";

import { useActiveOrg } from "@/utils/org-context";
import { JOB_STATUS_OPTIONS } from "@/lib/job-statuses";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";

import {
  CalendarProvider,
  ClientContainer,
  KeyDateSidePanel,
  toCalendarEvents,
  useAllOrgKeyDates,
  type KeyDatePanelMode,
  type TCalendarView,
} from "@/calendar";
import { TasksLoadError } from "./-ui.tasks-load-error";

/**
 * Same default visibility as the job calendar. Investigation lookback
 * windows are NOT calendar events — they live in the dedicated
 * `InvestigationWindowsPanel` on the job calendar page.
 */
const DEFAULT_VISIBLE_CATEGORIES: KeyDateCategory[] = [
  "appointment",
  "lodgement",
  "meeting",
  "milestone",
  "custom",
];

/**
 * Job-status filter, persisted per browser. Defaults to in-flight jobs
 * (pre-appointment / day one / active) so completed, archived, and on-hold
 * matters don't drown out live work; an empty selection means all statuses.
 */
const JOB_STATUS_FILTER_KEY = "morgan.calendar.jobStatusFilter";

function loadJobStatusFilter(): JobStatus[] {
  const known = new Set<string>(JOB_STATUS_OPTIONS.map((o) => o.value));
  try {
    const raw = localStorage.getItem(JOB_STATUS_FILTER_KEY);
    if (!raw) return [...ACTIVE_JOB_STATUSES];
    const parsed = JSON.parse(raw) as string[];
    if (!Array.isArray(parsed)) return [...ACTIVE_JOB_STATUSES];
    return parsed.filter((s): s is JobStatus => known.has(s));
  } catch {
    return [...ACTIVE_JOB_STATUSES];
  }
}

function persistJobStatusFilter(statuses: ReadonlyArray<JobStatus>): void {
  try {
    localStorage.setItem(JOB_STATUS_FILTER_KEY, JSON.stringify(statuses));
  } catch {
    // localStorage unavailable (private mode) — the filter just won't persist.
  }
}

/**
 * Hide-subtasks toggle, persisted per browser (MOR-752). Shared with the
 * job calendar so the preference follows the user across calendars.
 */
const HIDE_SUBTASKS_KEY = "morgan.calendar.hideSubtasks";

function loadHideSubtasks(): boolean {
  try {
    return localStorage.getItem(HIDE_SUBTASKS_KEY) === "true";
  } catch {
    return false;
  }
}

function persistHideSubtasks(value: boolean): void {
  try {
    localStorage.setItem(HIDE_SUBTASKS_KEY, String(value));
  } catch {
    // localStorage unavailable (private mode) — the toggle just won't persist.
  }
}

import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { Check, ChevronsUpDown, Filter } from "lucide-react";
import { cn } from "@/utils/misc";

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/calendar")({
  component: WorkspaceCalendarPage,
  errorComponent: TasksLoadError,
});

function WorkspaceCalendarPage() {
  const navigate = useNavigate();
  const { activeOrg } = useActiveOrg();

  const [view, setView] = useState<TCalendarView>("week");
  const [panel, setPanel] = useState<KeyDatePanelMode>({ kind: "closed" });
  const [selectedJobIds, setSelectedJobIds] = useState<Id<"jobs">[]>([]);
  const [jobStatuses, setJobStatuses] =
    useState<JobStatus[]>(loadJobStatusFilter);
  const [visibleCategories, setVisibleCategories] = useState<KeyDateCategory[]>(
    DEFAULT_VISIBLE_CATEGORIES,
  );
  const [hideSubtasks, setHideSubtasks] = useState<boolean>(loadHideSubtasks);
  const handleHideSubtasksChange = (next: boolean) => {
    setHideSubtasks(next);
    persistHideSubtasks(next);
  };

  const { data: jobs } = useQuery(
    convexQueryOrIdle(
      api.jobs.queries.listJobs,
      activeOrg ? { orgId: activeOrg._id } : undefined,
    ),
  );

  // Events stream in pages (MOR-2473): the first page paints the calendar,
  // the rest arrive in the background while `isLoadingAll` holds.
  const {
    events,
    isLoading: eventsLoading,
    isLoadingAll,
  } = useAllOrgKeyDates({
    orgId: activeOrg?._id,
    jobIds: selectedJobIds,
    jobStatuses,
  });

  const calendarEvents = useMemo(() => toCalendarEvents(events), [events]);

  // The job combobox only offers jobs that pass the status filter, so a
  // narrowed status selection can't leave invisible job constraints behind.
  const jobOptions = useMemo(() => {
    const statusSet = new Set<string>(jobStatuses);
    return (jobs ?? [])
      .filter((j) => statusSet.size === 0 || statusSet.has(j.status))
      .map((j) => ({ id: j._id, name: j.entityName }));
  }, [jobs, jobStatuses]);

  const handleJobStatusesChange = (next: JobStatus[]) => {
    setJobStatuses(next);
    persistJobStatusFilter(next);
    // Drop job selections that the new status filter hides.
    if (selectedJobIds.length > 0 && jobs) {
      const statusSet = new Set<string>(next);
      const visible = new Set(
        jobs
          .filter((j) => statusSet.size === 0 || statusSet.has(j.status))
          .map((j) => j._id.toString()),
      );
      setSelectedJobIds(
        selectedJobIds.filter((id) => visible.has(id.toString())),
      );
    }
  };

  const handleEventClick = (eventId: string | number) => {
    const row = events.find((e) => e.id === String(eventId));
    if (row) setPanel({ kind: "view", event: row });
  };

  const handleDateCellClick = (date: Date, endDate?: Date) => {
    const defaultJobId = selectedJobIds[0] ?? jobOptions[0]?.id ?? undefined;
    if (!defaultJobId) return;
    const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0;
    setPanel({
      kind: "create",
      jobId: defaultJobId,
      initialDate: format(date, "yyyy-MM-dd"),
      initialTime: hasTime ? format(date, "HH:mm") : undefined,
      initialEndTime: endDate ? format(endDate, "HH:mm") : undefined,
    });
  };

  if (!activeOrg) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Select an organisation to view its calendar.
      </div>
    );
  }

  if (eventsLoading || jobs === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <CalendarProvider
        view={view}
        onViewChange={setView}
        events={calendarEvents}
        onEventClick={(event) => handleEventClick(event.id)}
        onDateCellClick={handleDateCellClick}
        showJobName
      >
        <ClientContainer
          visibleCategories={visibleCategories}
          onVisibleCategoriesChange={setVisibleCategories}
          hideSubtasks={hideSubtasks}
          onHideSubtasksChange={handleHideSubtasksChange}
          /* Org has no jobs yet — hide the add button rather than
             open a broken side panel without a job target. */
          hideAddButton={jobOptions.length === 0}
          /* The ICS feed always covers every job the user can see, so the
             Subscribe button lives on the workspace calendar only. */
          showSubscribe
          /* Page title + description + job filter live at the top of the
             calendar header card. */
          headerSlot={
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">
                  Calendar
                </h1>
                <p className="text-sm text-muted-foreground">
                  Every event &amp; task deadline across your jobs. Click to see
                  details or add a new event or task for any job.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {isLoadingAll && (
                  <span
                    role="status"
                    className="flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <Spinner className="h-3.5 w-3.5" />
                    Loading more…
                  </span>
                )}
                <JobStatusFilter
                  selected={jobStatuses}
                  onChange={handleJobStatusesChange}
                />
                <JobFilter
                  options={jobOptions}
                  selectedIds={selectedJobIds}
                  onChange={setSelectedJobIds}
                />
              </div>
            </div>
          }
        />
      </CalendarProvider>

      {/* Create / view / edit all live in the side panel (MOR-707 follow-up):
          the CreatePanel owns the Type-aware "Add new" flow. */}
      <KeyDateSidePanel
        state={panel}
        onClose={() => setPanel({ kind: "closed" })}
        jobOptions={jobOptions}
      />

      {/* When the user views an event in workspace mode we want a quick
          jump to the underlying job. Rendered inline so the side panel
          only owns the basics. */}
      {panel.kind === "view" && (
        <div className="fixed bottom-4 right-4 z-50">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              navigate({
                to: "/dashboard/jobs/$jobId/calendar",
                params: { jobId: panel.event.jobId },
              })
            }
          >
            Open {panel.event.jobName} →
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Job status filter (multi-select combobox)
// ---------------------------------------------------------------------------

const ACTIVE_STATUS_SET = new Set<string>(ACTIVE_JOB_STATUSES);

function JobStatusFilter({
  selected,
  onChange,
}: {
  selected: JobStatus[];
  onChange: (statuses: JobStatus[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set<string>(selected), [selected]);

  const isActiveDefault =
    selected.length === ACTIVE_STATUS_SET.size &&
    selected.every((s) => ACTIVE_STATUS_SET.has(s));

  const label =
    selected.length === 0
      ? "All job statuses"
      : isActiveDefault
        ? "Active jobs"
        : `${selected.length} status${selected.length === 1 ? "" : "es"}`;

  const toggle = (status: JobStatus) => {
    if (selectedSet.has(status)) {
      onChange(selected.filter((existing) => existing !== status));
    } else {
      onChange([...selected, status]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="min-w-[180px] justify-between"
        >
          <span className="flex items-center gap-2">
            <Filter className="size-4" />
            {label}
          </span>
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="end">
        <Command>
          <CommandList>
            <CommandGroup>
              {JOB_STATUS_OPTIONS.map((option) => {
                const isSelected = selectedSet.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className="flex items-center justify-between border-t px-2 py-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange([...ACTIVE_JOB_STATUSES])}
          >
            Active only
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => onChange([])}
          >
            All statuses
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Job filter (multi-select combobox)
// ---------------------------------------------------------------------------

function JobFilter({
  options,
  selectedIds,
  onChange,
}: {
  options: Array<{ id: Id<"jobs">; name: string }>;
  selectedIds: Id<"jobs">[];
  onChange: (ids: Id<"jobs">[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(
    () => new Set(selectedIds.map((id) => id.toString())),
    [selectedIds],
  );

  const toggle = (id: Id<"jobs">) => {
    if (selectedSet.has(id.toString())) {
      onChange(selectedIds.filter((existing) => existing !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="min-w-[220px] justify-between"
        >
          <span className="flex items-center gap-2">
            <Filter className="size-4" />
            {selectedIds.length === 0
              ? "All jobs"
              : `${selectedIds.length} job${selectedIds.length === 1 ? "" : "s"}`}
            {selectedIds.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {selectedIds.length}
              </Badge>
            )}
          </span>
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Filter jobs…" />
          <CommandList>
            <CommandEmpty>No jobs found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const selected = selectedSet.has(option.id.toString());
                return (
                  <CommandItem
                    key={option.id}
                    value={option.name}
                    onSelect={() => toggle(option.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        selected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.name}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
